/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Intent:     shop owner blocks when a professional/cabin is unavailable — workbench
 * Hierarchy:  readable absence rows lead; add form is secondary until opened
 * Palette:    agenda tokens (teal / cool gray-green) — no mint wash
 * Depth:      borders + hairlines only
 * Surfaces:   list rows surface2; picker triggers inset surface
 * Typography: Plex Sans labels; short es-AR ranges
 * Spacing:    8px base, tight
 */
import React, { createElement, Fragment, useMemo, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { getShellMainPanelWidth } from '../../shared/layout/useContentLayout';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { iosDatePickerDisplay } from '../../shared/ui/iosDatePickerDisplay'
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding'
import { webNativeTemporalInputStyle } from '../../shared/ui/webNativeTemporalInput';
import { agenda, agendaFonts } from '../domain/agendaTheme';
import type { ResourceTimeOff } from '../domain/types';

export type ResourceTimeOffCreateInput = {
  startsAt: string;
  endsAt: string;
  reason: string | null;
};

type Props = {
  items: ResourceTimeOff[];
  loading?: boolean;
  creating?: boolean;
  deleting?: boolean;
  /** Hide the explanatory caption when the parent already provides a section title. */
  hideCaption?: boolean;
  /** Override the empty-state label. */
  emptyLabel?: string;
  /** Panel-aware wide layout from parent. */
  isWide?: boolean;
  onCreate: (input: ResourceTimeOffCreateInput) => void | Promise<void>;
  onDelete: (id: string) => void | Promise<void>;
};

const DATE_OPTS: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
const TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};

/** Local calendar day at 00:00 (device TZ). */
function startOfLocalDay(d: Date = new Date()): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/** Exclusive end of a full local calendar day (next midnight). */
function endOfLocalDayExclusive(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);
}

/** Default absence = today’s full local day (00:00 → tomorrow 00:00). */
function defaultStart(): Date {
  return startOfLocalDay();
}

function defaultEnd(from: Date = defaultStart()): Date {
  return endOfLocalDayExclusive(from);
}

function isFullLocalDayRange(start: Date, end: Date): boolean {
  return (
    start.getHours() === 0 &&
    start.getMinutes() === 0 &&
    start.getSeconds() === 0 &&
    end.getTime() === endOfLocalDayExclusive(start).getTime()
  );
}

function toDateInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toTimeInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDateInputValue(value: string, base: Date): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const next = new Date(base);
  next.setFullYear(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return next;
}

function fromTimeInputValue(value: string, base: Date): Date | null {
  const m = /^(\d{2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const next = new Date(base);
  next.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return next;
}

export function formatTimeOffRangeLabel(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${startsAt} – ${endsAt}`;
  }
  if (isFullLocalDayRange(start, end)) {
    return `${start.toLocaleDateString('es-AR', DATE_OPTS)} · todo el día`;
  }
  // Multi-day full-day span: 00:00 → exclusive midnight N days later.
  if (
    start.getHours() === 0 &&
    start.getMinutes() === 0 &&
    end.getHours() === 0 &&
    end.getMinutes() === 0 &&
    end.getTime() > start.getTime()
  ) {
    const lastInclusive = new Date(end.getTime() - 1);
    const sd = start.toLocaleDateString('es-AR', DATE_OPTS);
    const ed = lastInclusive.toLocaleDateString('es-AR', DATE_OPTS);
    if (sd === ed) return `${sd} · todo el día`;
    return `${sd} – ${ed} · todo el día`;
  }
  const sd = start.toLocaleDateString('es-AR', DATE_OPTS);
  const ed = end.toLocaleDateString('es-AR', DATE_OPTS);
  const st = start.toLocaleTimeString('es-AR', TIME_OPTS);
  const et = end.toLocaleTimeString('es-AR', TIME_OPTS);
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  if (sameDay) return `${sd} · ${st} – ${et}`;
  return `${sd} ${st} – ${ed} ${et}`;
}

function formatBoundLabel(d: Date): string {
  return `${d.toLocaleDateString('es-AR', DATE_OPTS)} · ${d.toLocaleTimeString('es-AR', TIME_OPTS)}`;
}

type BoundFieldProps = {
  label: string;
  value: Date;
  onChange: (next: Date) => void;
};

function mergeDatePart(base: Date, picked: Date): Date {
  const next = new Date(base);
  next.setFullYear(picked.getFullYear(), picked.getMonth(), picked.getDate());
  return next;
}

function mergeTimePart(base: Date, picked: Date): Date {
  const next = new Date(base);
  next.setHours(picked.getHours(), picked.getMinutes(), 0, 0);
  return next;
}

function TimeOffBoundField({ label, value, onChange }: BoundFieldProps) {
  const iosMode = iosDatePickerDisplay();
  const iosModalBackdropPadding = useModalBackdropPadding();
  const [androidMode, setAndroidMode] = useState<'date' | 'time' | null>(null);
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const [iosDraft, setIosDraft] = useState(value);

  if (Platform.OS === 'web') {
    return (
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>{label}</Text>
        <View style={styles.boundRow}>
          {createElement('input', {
            type: 'date',
            value: toDateInputValue(value),
            onChange: (e: { target: { value: string } }) => {
              const next = fromDateInputValue(e.target.value ?? '', value);
              if (next) onChange(next);
            },
            style: webDateInputStyle,
            'aria-label': `${label}, fecha`,
          })}
          {createElement('input', {
            type: 'time',
            value: toTimeInputValue(value),
            onChange: (e: { target: { value: string } }) => {
              const next = fromTimeInputValue(e.target.value ?? '', value);
              if (next) onChange(next);
            },
            style: webTimeInputStyle,
            'aria-label': `${label}, hora`,
          })}
        </View>
      </View>
    );
  }

  if (Platform.OS === 'android') {
    return (
      <Fragment>
        <View style={styles.field}>
          <Text style={styles.fieldLabel}>{label}</Text>
          <View style={styles.boundRow}>
            <TouchableOpacity
              style={styles.boundTrigger}
              onPress={() => setAndroidMode('date')}
              accessibilityRole="button"
              accessibilityLabel={`${label}, fecha`}
            >
              <Text style={styles.boundValue}>
                {value.toLocaleDateString('es-AR', DATE_OPTS)}
              </Text>
              <Text style={styles.boundHint}>Fecha</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.boundTrigger}
              onPress={() => setAndroidMode('time')}
              accessibilityRole="button"
              accessibilityLabel={`${label}, hora`}
            >
              <Text style={styles.boundValue}>
                {value.toLocaleTimeString('es-AR', TIME_OPTS)}
              </Text>
              <Text style={styles.boundHint}>Hora</Text>
            </TouchableOpacity>
          </View>
        </View>
        {androidMode ? (
          <DateTimePicker
            value={value}
            mode={androidMode}
            display="default"
            is24Hour
            locale="es-AR"
            onValueChange={(_e, date) => {
              const mode = androidMode;
              setAndroidMode(null);
              if (!date) return;
              onChange(mode === 'date' ? mergeDatePart(value, date) : mergeTimePart(value, date));
            }}
            onDismiss={() => setAndroidMode(null)}
          />
        ) : null}
      </Fragment>
    );
  }

  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      {iosMode === 'inline' ? (
        <View style={styles.boundRow}>
          <DateTimePicker
            value={value}
            mode="date"
            display="compact"
            locale="es-AR"
            onValueChange={(_e, date) => {
              if (date) onChange(mergeDatePart(value, date));
            }}
            style={styles.iosCompact}
          />
          <DateTimePicker
            value={value}
            mode="time"
            display="compact"
            locale="es-AR"
            onValueChange={(_e, date) => {
              if (date) onChange(mergeTimePart(value, date));
            }}
            style={styles.iosCompact}
          />
        </View>
      ) : (
        <>
          <TouchableOpacity
            style={styles.boundTriggerWide}
            onPress={() => {
              setIosDraft(value);
              setIosSheetOpen(true);
            }}
            accessibilityRole="button"
            accessibilityLabel={label}
          >
            <Text style={styles.boundValue}>{formatBoundLabel(value)}</Text>
          </TouchableOpacity>
          <Modal
            visible={iosSheetOpen}
            transparent
            animationType="fade"
            onRequestClose={() => setIosSheetOpen(false)}
          >
            <View style={[styles.iosBackdrop, iosModalBackdropPadding]}>
              <View style={styles.iosCard}>
                <Text style={styles.iosTitle}>{label}</Text>
                <DateTimePicker
                  value={iosDraft}
                  mode="datetime"
                  display="spinner"
                  locale="es-AR"
                  onValueChange={(_e, date) => {
                    if (date) setIosDraft(date);
                  }}
                  style={styles.iosSpinner}
                />
                <View style={styles.iosActions}>
                  <TouchableOpacity
                    style={styles.outlineBtn}
                    onPress={() => setIosSheetOpen(false)}
                  >
                    <Text style={styles.outlineBtnText}>Cancelar</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.primaryBtn}
                    onPress={() => {
                      onChange(iosDraft);
                      setIosSheetOpen(false);
                    }}
                  >
                    <Text style={styles.primaryBtnText}>Listo</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </Modal>
        </>
      )}
    </View>
  );
}

const webDateInputStyle = webNativeTemporalInputStyle({
  size: 'comfortable',
  flex: 1,
  minWidth: 148,
  fontWeight: '600',
  fontFamily: agendaFonts.sans,
});

const webTimeInputStyle = webNativeTemporalInputStyle({
  size: 'comfortable',
  flex: 0,
  minWidth: 128,
  width: 128,
  fontWeight: '600',
  fontFamily: agendaFonts.mono,
});

export function ResourceTimeOffEditor({
  items,
  loading = false,
  creating = false,
  deleting = false,
  hideCaption = false,
  emptyLabel = 'Sin ausencias cargadas.',
  isWide: isWideProp,
  onCreate,
  onDelete,
}: Props) {
  const { width } = useWindowDimensions();
  const isWide =
    isWideProp ?? getShellMainPanelWidth(width) >= CONTENT_WIDE_BREAKPOINT;
  const [showForm, setShowForm] = useState(false);
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(() => defaultEnd());
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ResourceTimeOff | null>(null);

  const sorted = useMemo(
    () => items.slice().sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    [items],
  );

  const openForm = () => {
    const s = defaultStart();
    setStart(s);
    setEnd(defaultEnd(s));
    setReason('');
    setFormError(null);
    setShowForm(true);
  };

  const submit = async () => {
    if (!(end.getTime() > start.getTime())) {
      setFormError('La fecha de fin debe ser posterior al inicio.');
      return;
    }
    setFormError(null);
    try {
      await onCreate({
        startsAt: start.toISOString(),
        endsAt: end.toISOString(),
        reason: reason.trim() || null,
      });
      setShowForm(false);
      setReason('');
    } catch {
      // Parent surfaces the error; keep the form open.
    }
  };

  return (
    <View style={styles.wrap}>
      {!hideCaption ? (
        <Text style={styles.caption}>
          Vacaciones o día suelto. Cubre el día entero si no ponés hora.
        </Text>
      ) : null}

      {loading ? (
        <Text style={styles.meta}>Cargando…</Text>
      ) : sorted.length === 0 ? (
        emptyLabel ? <Text style={styles.empty}>{emptyLabel}</Text> : null
      ) : (
        <View style={styles.list}>
          {sorted.map((t, index) => (
            <View
              key={t.id}
              style={[styles.row, index < sorted.length - 1 && styles.rowDivider]}
            >
              <View style={styles.rowBody}>
                <Text style={styles.rowRange}>
                  {formatTimeOffRangeLabel(t.startsAt, t.endsAt)}
                </Text>
                {t.reason ? <Text style={styles.rowReason}>{t.reason}</Text> : null}
              </View>
              <TouchableOpacity
                onPress={() => setDeleteTarget(t)}
                hitSlop={10}
                accessibilityRole="button"
                accessibilityLabel="Eliminar ausencia"
                style={styles.deleteHit}
              >
                <Text style={styles.deleteText}>Eliminar</Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}

      {showForm ? (
        <View style={styles.form}>
          <View style={isWide ? styles.formRow : undefined}>
            <View style={isWide ? styles.formCol : undefined}>
              <TimeOffBoundField
                label="Inicio"
                value={start}
                onChange={(next) => {
                  const keepFullDay = isFullLocalDayRange(start, end);
                  setStart(next);
                  if (keepFullDay) {
                    setEnd(endOfLocalDayExclusive(next));
                  } else if (!(end.getTime() > next.getTime())) {
                    setEnd(defaultEnd(next));
                  }
                }}
              />
            </View>
            <View style={isWide ? styles.formCol : undefined}>
              <TimeOffBoundField label="Fin" value={end} onChange={setEnd} />
            </View>
            {isWide ? (
              <TouchableOpacity
                style={[styles.outlineBtn, styles.todoElDiaWide]}
                onPress={() => {
                  const day = startOfLocalDay(start);
                  setStart(day);
                  setEnd(endOfLocalDayExclusive(day));
                  setFormError(null);
                }}
                accessibilityRole="button"
                accessibilityLabel="Usar todo el día"
              >
                <Text style={styles.outlineBtnText}>Todo el día</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          {!isWide ? (
            <TouchableOpacity
              style={styles.outlineBtn}
              onPress={() => {
                const day = startOfLocalDay(start);
                setStart(day);
                setEnd(endOfLocalDayExclusive(day));
                setFormError(null);
              }}
              accessibilityRole="button"
              accessibilityLabel="Usar todo el día"
            >
              <Text style={styles.outlineBtnText}>Todo el día</Text>
            </TouchableOpacity>
          ) : null}
          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Motivo (opcional)</Text>
            <TextInput
              style={styles.input}
              value={reason}
              onChangeText={setReason}
              placeholder="Ej. Vacaciones"
              placeholderTextColor={agenda.ink2}
              editable={!creating}
            />
          </View>
          {formError ? <Text style={styles.error}>{formError}</Text> : null}
          <View style={styles.formActions}>
            <TouchableOpacity
              style={[styles.outlineBtn, creating && styles.disabled]}
              onPress={() => setShowForm(false)}
              disabled={creating}
              accessibilityRole="button"
            >
              <Text style={styles.outlineBtnText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.primaryBtnFlex, creating && styles.disabled]}
              onPress={() => void submit()}
              disabled={creating}
              accessibilityRole="button"
            >
              <Text style={styles.primaryBtnText}>{creating ? 'Agregando…' : 'Agregar'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        <TouchableOpacity
          style={styles.outlineBtn}
          onPress={openForm}
          accessibilityRole="button"
        >
          <Text style={styles.outlineBtnText}>Agregar ausencia</Text>
        </TouchableOpacity>
      )}

      <OperationalConfirmModal
        visible={deleteTarget != null}
        title="Eliminar ausencia"
        entityName={
          deleteTarget
            ? formatTimeOffRangeLabel(deleteTarget.startsAt, deleteTarget.endsAt)
            : undefined
        }
        message="Se vuelve a ofrecer esa franja para turnos y reservas online."
        confirmLabel="Eliminar"
        destructive
        iconName="calendar-outline"
        loading={deleting}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await onDelete(deleteTarget.id);
            setDeleteTarget(null);
          } catch {
            // Parent surfaces the error; keep the confirm open.
          }
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: 10, marginTop: 4 },
  caption: {
    fontSize: 12,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
    lineHeight: 17,
  },
  meta: {
    fontSize: 12,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  empty: {
    fontSize: 13,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
    paddingVertical: 4,
  },
  list: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: agenda.surface2,
  },
  rowDivider: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: agenda.border,
  },
  rowBody: { flex: 1, minWidth: 0, gap: 2 },
  rowRange: {
    fontSize: 13,
    fontWeight: '600',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  rowReason: {
    fontSize: 12,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  deleteHit: {
    minHeight: 40,
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  deleteText: {
    color: agenda.noShow.text,
    fontWeight: '600',
    fontSize: 13,
    fontFamily: agendaFonts.sans,
  },
  form: { gap: 8, marginTop: 2 },
  formRow: { flexDirection: 'row', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' },
  formCol: { flex: 1, minWidth: 280 },
  todoElDiaWide: { alignSelf: 'flex-end', marginBottom: 0 },
  field: { gap: 6 },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  boundRow: { flexDirection: 'row', gap: 8, alignItems: 'center' },
  boundTrigger: {
    flex: 1,
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface2,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
    justifyContent: 'center',
    gap: 2,
  },
  boundTriggerWide: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface2,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
    justifyContent: 'center',
    gap: 2,
  },
  boundValue: {
    fontSize: 14,
    fontWeight: '600',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  boundHint: {
    fontSize: 11,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  iosCompact: { alignSelf: 'flex-start' },
  iosBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(26,31,30,0.4)',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  iosCard: {
    backgroundColor: agenda.surface,
    borderRadius: agenda.radius,
    padding: 14,
    gap: 10,
    borderWidth: 1,
    borderColor: agenda.border,
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  iosTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  iosSpinner: { alignSelf: 'center', width: '100%', height: 216 },
  iosActions: { flexDirection: 'row', gap: 8, justifyContent: 'flex-end' },
  input: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
    color: agenda.ink,
    backgroundColor: agenda.surface2,
    fontSize: 15,
    fontFamily: agendaFonts.sans,
  },
  error: {
    fontSize: 12,
    color: agenda.noShow.text,
    fontFamily: agendaFonts.sans,
  },
  formActions: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 2 },
  outlineBtn: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface,
    borderWidth: 1,
    borderColor: agenda.border,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  outlineBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  primaryBtn: {
    backgroundColor: agenda.brand,
    borderRadius: agenda.radius,
    paddingVertical: 10,
    paddingHorizontal: 14,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnFlex: {
    flex: 1,
    backgroundColor: agenda.brand,
    borderRadius: agenda.radius,
    paddingVertical: 10,
    paddingHorizontal: 14,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnText: {
    fontWeight: '700',
    color: agenda.brandInk,
    fontFamily: agendaFonts.sans,
  },
  disabled: { opacity: 0.6 },
});
